import { useCallback, useMemo, useState } from "react";
import { push } from "connected-react-router";
import { isEmpty, isFunction, keys, pick } from "lodash";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useDebouncedEffect } from "@lkf/hooks";
import { parse, stringify } from "@tootools/query-string";

export interface LocationSearchParams {
  [key: string]: any;
}

/**
 * `param` type will vary depending on how `query-string` parses it (e.g. "test" -> string, "123" -> number)
 * Its type won't be casted to match the TargetType, thus its type is unknown
 */
export function useSearchParam<TargetType>(
  field: string,
  initial: TargetType,
  transformer?: (value: any) => TargetType
) {
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useMemo(() => parse(location.search), [location.search]);
  const _setParam = useCallback(
    (updatedParam: TargetType) => {
      dispatch(
        push({
          search: stringify({
            ...params,
            [field]: updatedParam,
          }),
        })
      );
    },
    [dispatch, field, params]
  );

  const rawParam = params[field];
  const paramFromParams =
    isFunction(transformer) && rawParam !== undefined
      ? transformer(rawParam)
      : (rawParam as any); // Ideally we would leave as unknown or require the transformer function

  return {
    param: paramFromParams || initial,
    setParam: _setParam,
  };
}

export function useSearchParams<Params extends Record<string, unknown>>(
  initial: Params
) {
  const location = useLocation();
  const dispatch = useDispatch();

  const allParams = useMemo(() => parse(location.search), [location.search]);

  const setParams = useCallback(
    (updatedParams: Params) => {
      dispatch(push({ search: stringify({ ...allParams, ...updatedParams }) }));
    },
    [dispatch, allParams]
  );
  const params = useMemo(
    () => pick(allParams, keys(initial)) as Params,
    [allParams, initial]
  );

  return { params: isEmpty(params) ? initial : params, setParams };
}

export function useSearchParamDebounced<TargetType>(
  field: string,
  initial: TargetType,
  debounceMs = 250,
  transformer?: (param: any) => TargetType
) {
  const { param, setParam } = useSearchParam(field, initial, transformer);
  const [value, setValue] = useState(param);

  useDebouncedEffect(
    () => {
      setParam(value);
    },
    debounceMs,
    [value]
  );

  return {
    param: value,
    setParam: setValue,
  };
}
